import { ReactNode } from "react";
import { useTranslate } from "@refinedev/core";
import { Alert, Flex, Typography, Upload, UploadFile } from "antd";
import { CloseOutlined, InboxOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { Button, IconButton } from "@libertify/ui";
import {
  DOCUMENT_ACCEPT,
  formatFileSize,
} from "../../../create-experience/helpers/fileList";

/**
 * Same ceiling as the non-LTI document uploader (ProjectDocumentUpload).
 * Exported because the hard cap has to be applied where the staged list lives:
 * antd's own `maxCount` can't do it here — it runs `beforeUpload` for every
 * picked file first and only trims its internal list, which this uncontrolled
 * dragger never reads. Disabling the dragger below stops the NEXT batch; the
 * page clamps the current one.
 */
export const MAX_DOCUMENTS = 35;

export type DeepLinkUploaderProps = {
  /** Documents staged so far, in order (the first drives the project title). */
  files: UploadFile[];
  error?: string | null;
  onStageFile: (file: File) => void;
  onRemoveFile: (file: UploadFile) => void;
  onCreate: () => void;
  /** The "or attach an existing project" section, rendered below the button. */
  children?: ReactNode;
};

/**
 * The deep-link page's select phase: drop target, staged document list and the
 * Create button. Presentational on purpose — the page owns the staged files and
 * the create/upload sequence, so this renders inside Storybook without Refine's
 * data layer or an LTI session.
 */
export const DeepLinkUploader = ({
  files,
  error,
  onStageFile,
  onRemoveFile,
  onCreate,
  children,
}: DeepLinkUploaderProps) => {
  const t = useTranslate();
  const hasFiles = files.length > 0;
  const isFull = files.length >= MAX_DOCUMENTS;

  return (
    <>
      <Typography.Title level={2}>{t("lti.deepLink.title")}</Typography.Title>
      <Typography.Paragraph>{t("lti.deepLink.subtitle")}</Typography.Paragraph>
      {error && (
        <Alert type="error" message={error} style={{ marginBottom: 16 }} />
      )}

      <StyledDragger
        $compact={hasFiles}
        accept={DOCUMENT_ACCEPT}
        multiple
        disabled={isFull}
        showUploadList={false}
        beforeUpload={(file) => {
          onStageFile(file);
          return false;
        }}
      >
        {hasFiles ? null : (
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
        )}
        <p className="ant-upload-text">
          {isFull
            ? t("lti.deepLink.maxFiles", { max: MAX_DOCUMENTS })
            : t(hasFiles ? "lti.deepLink.addMore" : "lti.deepLink.uploadHint")}
        </p>
        {hasFiles ? null : (
          <p className="ant-upload-hint">
            {t("ProjectCreateWizard.createUploaderFormats")}
          </p>
        )}
      </StyledDragger>

      {hasFiles ? (
        // Height-capped so a long list scrolls here instead of pushing the
        // Create button out of the ~710px-tall Moodle frame.
        <Flex
          vertical
          component="ul"
          style={{
            maxHeight: 200,
            overflowY: "auto",
            margin: "12px 0 0",
            padding: 0,
            listStyle: "none",
          }}
        >
          {files.map((file) => (
            <Flex
              key={file.uid}
              component="li"
              align="center"
              gap={8}
              style={{ padding: "4px 0" }}
            >
              <Typography.Text
                ellipsis
                title={file.name}
                style={{ flex: 1, minWidth: 0, fontSize: 13 }}
              >
                {file.name}
              </Typography.Text>
              <Typography.Text
                type="secondary"
                style={{ flexShrink: 0, fontSize: 12 }}
              >
                {formatFileSize(file.size)}
              </Typography.Text>
              <IconButton
                size="s"
                variant="utility"
                icon={<CloseOutlined />}
                aria-label={t("lti.deepLink.removeFile")}
                onClick={() => onRemoveFile(file)}
              />
            </Flex>
          ))}
        </Flex>
      ) : null}

      <Button
        variant="primary"
        block
        disabled={!hasFiles}
        onClick={onCreate}
        style={{ marginTop: 16 }}
      >
        {t("lti.deepLink.createButton")}
      </Button>

      {children}
    </>
  );
};

// ─── Styled components ───────────────────────────────────────────────────────

// The one thing neither antd nor the DS exposes as a prop: Dragger's internal
// padding. Once documents are staged the dragger stops being the hero and
// becomes an "add more" target, so it sheds its icon and most of its height —
// the Moodle LTI frame is only ~710px tall and the list below needs the room.
const StyledDragger = styled(Upload.Dragger)<{ $compact: boolean }>`
  ${({ $compact }) =>
    $compact &&
    `
    .ant-upload-btn {
      padding: 12px 0 !important;
    }
  `}
`;
